<template>
  <div>
      <!-- 机械臂可视化 - 使用teleport渲染到底部面板 -->
      <teleport to=".bottom-panel" v-if="showArmVisualization">
        <v-card variant="outlined" style="height: 100%; display: flex; flex-direction: column;">
          <v-card-title class="text-subtitle-1 d-flex align-center" style="flex-shrink: 0;">
            <span>机械臂可视化</span>
            <v-spacer></v-spacer>
            <v-btn 
              size="small" 
              color="primary" 
              variant="outlined"
              @click="emit('toggle-visualization', false)"
            >
              切换到角度图
            </v-btn>
          </v-card-title>
          <v-divider style="flex-shrink: 0;"></v-divider>
          <v-card-text style="flex: 1; overflow: hidden; display: flex; align-items: center; justify-content: center;">
            <!-- Canvas机械臂图 -->
            <canvas 
              ref="canvasRef" 
              width="500" 
              height="400"
              @mousedown="handleMouseDown"
              @mousemove="handleMouseMove"
              @mouseup="handleMouseUp"
              @mouseleave="handleMouseUp"
              @wheel.prevent="handleWheel"
              style="cursor: pointer;"
            ></canvas>
          </v-card-text>
        </v-card>
      </teleport>
      
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import * as echarts from 'echarts'
import trajectoryList from '../trajectoryList.json'
import servoList from '../servoList.json'
import {
  getServoIdForKinematicArmFromConfig,
  resolveServoNameForId,
  getKinematicAngleForPhysicalServoIndex,
  setKinematicArmFromPhysicalServoIndex
} from '../dualArmTrajectory.js'

const props = defineProps({
  deviceId: {
    type: String,
    default: ''
  },
  servoVersion: {
    type: String,
    default: ''
  },
  trajectoryConfig: {
    type: Object,
    default: null
  },
  trajectoryX: {
    type: Number,
    default: 0
  },
  trajectoryY: {
    type: Number,
    default: 0
  },
  trajectoryPoints: {
    type: Array,
    default: () => []
  },
  allServosData: {
    type: Array,
    default: () => []
  },
  showArmVisualization: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['trajectory-saved', 'exit', 'servos-changed', 'arm-config-changed', 'end-position-changed', 'toggle-visualization'])

const canvasRef = ref(null)
const chartRef = ref(null)
const isDragging = ref(false)
const showReachableArea = ref(true)
const showTrajectoryChart = ref(false) // 是否显示轨迹图（默认显示轨迹图）

// 舵机列表（从trajectoryConfig中获取）
const servos = ref([])
let chartInstance = null

// 机械臂配置（从trajectoryConfig中获取）
const armConfig = ref({
  L1: 0,
  L2: 0,
  L3: 0,
  L4: 0,
  distance: 0,
  servoA: 0,
  servoB: 0
})

// 末端执行器位置
const endEffectorPosition = ref({ x: 1.19, y: -4.42 })

let ctx = null
let centerX = 0
let centerY = 0
let baseScale = 4 // 像素到单位的比例（缩小到原来的10%）
const zoom = ref(1) // 缩放倍数
const scale = computed(() => baseScale * zoom.value)

// 保存轨迹按钮是否可用
const canSaveTrajectory = computed(() => {
  return props.deviceId && props.servoVersion
})

// 轨迹标题
const trajectoryTitle = computed(() => {
  return props.trajectoryConfig?.name || '末端位置'
})

// 根据零位名称获取坐标（与App.vue中的函数保持一致）
const getOriginPosition = (trajectoryConfig, originName = 'origin') => {
  if (!trajectoryConfig) return { x: 0, y: 0 }
  
  // 如果是 'default'，使用向后兼容的 x, y
  if (originName === 'default') {
    return {
      x: Number((trajectoryConfig.x ?? 0).toFixed(1)),
      y: Number((trajectoryConfig.y ?? 0).toFixed(1))
    }
  }
  
  // 查找对应的零位
  const origin = trajectoryConfig[originName]
  if (origin && origin.x !== undefined && origin.y !== undefined) {
    return {
      x: Number((origin.x).toFixed(1)),
      y: Number((origin.y).toFixed(1))
    }
  }
  
  // 如果找不到，回退到向后兼容的 x, y
  return {
    x: Number((trajectoryConfig.x ?? 0).toFixed(1)),
    y: Number((trajectoryConfig.y ?? 0).toFixed(1))
  }
}

// 显示的X坐标（优先从 origin 读取）
const displayX = computed(() => {
  if (props.trajectoryConfig) {
    const originPos = getOriginPosition(props.trajectoryConfig, 'origin')
    if (originPos.x !== 0 || originPos.y !== 0 || props.trajectoryConfig.origin) {
      return originPos.x
    }
    return props.trajectoryConfig.x ?? endEffectorPosition.value.x
  }
  return endEffectorPosition.value.x
})

// 显示的Y坐标（优先从 origin 读取）
const displayY = computed(() => {
  if (props.trajectoryConfig) {
    const originPos = getOriginPosition(props.trajectoryConfig, 'origin')
    if (originPos.x !== 0 || originPos.y !== 0 || props.trajectoryConfig.origin) {
      return originPos.y
    }
    return props.trajectoryConfig.y ?? endEffectorPosition.value.y
  }
  return endEffectorPosition.value.y
})

// 舵机A/B 标签（与 IK A/B 一致；右嘴角与物理 id 顺序交叉）
const servoALabel = computed(() => {
  const cfg = props.trajectoryConfig
  if (!cfg?.ids?.length) return '舵机A(左)'
  const id = getServoIdForKinematicArmFromConfig(cfg, 'A')
  return resolveServoNameForId(id, cfg.servoVersions) || '舵机A(左)'
})

const servoBLabel = computed(() => {
  const cfg = props.trajectoryConfig
  if (!cfg?.ids?.length) return '舵机B(右)'
  const id = getServoIdForKinematicArmFromConfig(cfg, 'B')
  return resolveServoNameForId(id, cfg.servoVersions) || '舵机B(右)'
})

// 可编辑的X和Y坐标
const editableX = ref(0)
const editableY = ref(0)
const isUpdatingFromProps = ref(false) // 标记是否正在从props更新

// 监听displayX和displayY的变化，同步到editableX和editableY
watch(() => displayX.value, (newVal) => {
  editableX.value = newVal
})

watch(() => displayY.value, (newVal) => {
  editableY.value = newVal
})

// 初始化editableX和editableY
watch(() => props.trajectoryConfig, (newConfig) => {
  if (newConfig) {
    // 优先从 origin 读取，如果没有则从 x, y 读取（向后兼容）
    const originPos = getOriginPosition(newConfig, 'origin')
    editableX.value = originPos.x !== 0 || originPos.y !== 0 || newConfig.origin ? originPos.x : (newConfig.x ?? endEffectorPosition.value.x)
    editableY.value = originPos.x !== 0 || originPos.y !== 0 || newConfig.origin ? originPos.y : (newConfig.y ?? endEffectorPosition.value.y)
    
    // 更新臂长和间距配置
    if (newConfig.armLengths) {
      armConfig.value.L1 = newConfig.armLengths.L1
      armConfig.value.L2 = newConfig.armLengths.L2
      armConfig.value.L3 = newConfig.armLengths.L3
      armConfig.value.L4 = newConfig.armLengths.L4
      armConfig.value.distance = newConfig.servoDistance
      draw()
    }
  }
}, { immediate: true })

// 初始化画布
const initCanvas = () => {
  if (!canvasRef.value) return
  ctx = canvasRef.value.getContext('2d')
  centerX = canvasRef.value.width / 2
  centerY = canvasRef.value.height / 2
  
  // 如果提供了轨迹配置，使用配置中的力臂参数
  if (props.trajectoryConfig && props.trajectoryConfig.armLengths) {
    armConfig.value.L1 = props.trajectoryConfig.armLengths.L1
    armConfig.value.L2 = props.trajectoryConfig.armLengths.L2
    armConfig.value.L3 = props.trajectoryConfig.armLengths.L3
    armConfig.value.L4 = props.trajectoryConfig.armLengths.L4
    armConfig.value.distance = props.trajectoryConfig.servoDistance
  }
  
  draw()
}

// 逆运动学计算：根据末端位置计算舵机角度
const inverseKinematics = (x, y) => {
  const { L1, L2, L3, L4, distance } = armConfig.value
  
  // 舵机A（左）的固定点
  const servoA_X = -distance / 2
  const servoA_Y = 0
  
  // 舵机B（右）的固定点
  const servoB_X = distance / 2
  const servoB_Y = 0
  
  // 计算到两个舵机的距离
  const distA = Math.sqrt((x - servoA_X) ** 2 + (y - servoA_Y) ** 2)
  const distB = Math.sqrt((x - servoB_X) ** 2 + (y - servoB_Y) ** 2)
  
  // 检查是否在可达范围内
  const maxDistA = L1 + L2
  const minDistA = Math.abs(L1 - L2)
  const maxDistB = L3 + L4
  const minDistB = Math.abs(L3 - L4)
  
  if (distA < minDistA || distA > maxDistA || distB < minDistB || distB > maxDistB) {
    return null // 不可达
  }
  
  // 计算舵机A的角度（左边舵机，取左边的解）
  const angleA1 = Math.atan2(y - servoA_Y, x - servoA_X)
  const cosAngleA2 = (L1 ** 2 + distA ** 2 - L2 ** 2) / (2 * L1 * distA)
  const sinAngleA2 = Math.sqrt(1 - cosAngleA2 ** 2)
  // 左边舵机取左边的解（elbow out），使用负的sinAngleA2
  const angleA = angleA1 - Math.atan2(-sinAngleA2, cosAngleA2)
  
  // 计算舵机B的角度（右边舵机，取右边的解）
  const angleB1 = Math.atan2(y - servoB_Y, x - servoB_X)
  const cosAngleB2 = (L3 ** 2 + distB ** 2 - L4 ** 2) / (2 * L3 * distB)
  const sinAngleB2 = Math.sqrt(1 - cosAngleB2 ** 2)
  // 右边舵机取右边的解（elbow out），使用正的sinAngleB2
  const angleB = angleB1 - Math.atan2(sinAngleB2, cosAngleB2)
  
  return {
    servoA: angleA * 180 / Math.PI,
    servoB: angleB * 180 / Math.PI
  }
}

// 正运动学计算：根据舵机角度计算末端位置
const forwardKinematics = (servoA, servoB) => {
  const { L1, L2, L3, L4, distance } = armConfig.value
  
  // 舵机A（左）的固定点
  const servoA_X = -distance / 2
  const servoA_Y = 0
  
  // 舵机B（右）的固定点
  const servoB_X = distance / 2
  const servoB_Y = 0
  
  // 将角度转换为弧度
  const angleA = servoA * Math.PI / 180
  const angleB = servoB * Math.PI / 180
  
  // 计算中间关节位置
  const jointA_X = servoA_X + L1 * Math.cos(angleA)
  const jointA_Y = servoA_Y + L1 * Math.sin(angleA)
  
  const jointB_X = servoB_X + L3 * Math.cos(angleB)
  const jointB_Y = servoB_Y + L3 * Math.sin(angleB)
  
  // 计算两条手臂的交点（末端执行器）
  const dx = jointB_X - jointA_X
  const dy = jointB_Y - jointA_Y
  const dist = Math.sqrt(dx ** 2 + dy ** 2)
  
  if (dist > L2 + L4 || dist < Math.abs(L2 - L4)) {
    return null // 没有交点
  }
  
  // 计算交点位置
  const a = (L2 ** 2 - L4 ** 2 + dist ** 2) / (2 * dist)
  const h = Math.sqrt(L2 ** 2 - a ** 2)
  
  const px = jointA_X + a * dx / dist
  const py = jointA_Y + a * dy / dist
  
  const ex = px - h * dy / dist
  const ey = py + h * dx / dist
  
  return { x: ex, y: ey }
}

// 绘制坐标系
const drawCoordinateSystem = () => {
  if (!ctx || !canvasRef.value) return
  
  const canvasWidth = canvasRef.value.width
  const canvasHeight = canvasRef.value.height
  
  // 绘制X轴
  ctx.strokeStyle = '#888'
  ctx.lineWidth = 1
  ctx.setLineDash([5, 5]) // 虚线
  
  // X轴正方向
  ctx.beginPath()
  ctx.moveTo(centerX, centerY)
  ctx.lineTo(canvasWidth - 20, centerY)
  ctx.stroke()
  
  // X轴负方向
  ctx.beginPath()
  ctx.moveTo(centerX, centerY)
  ctx.lineTo(20, centerY)
  ctx.stroke()
  
  // X轴箭头
  ctx.setLineDash([]) // 实线
  ctx.fillStyle = '#888'
  ctx.beginPath()
  ctx.moveTo(canvasWidth - 20, centerY)
  ctx.lineTo(canvasWidth - 30, centerY - 5)
  ctx.lineTo(canvasWidth - 30, centerY + 5)
  ctx.closePath()
  ctx.fill()
  
  // X轴标签
  ctx.fillStyle = '#000'
  ctx.font = '12px Arial'
  ctx.fillText('X', canvasWidth - 15, centerY - 10)
  
  // 绘制Y轴
  ctx.strokeStyle = '#888'
  ctx.lineWidth = 1
  ctx.setLineDash([5, 5]) // 虚线
  
  // Y轴正方向（向上）
  ctx.beginPath()
  ctx.moveTo(centerX, centerY)
  ctx.lineTo(centerX, 20)
  ctx.stroke()
  
  // Y轴负方向（向下）
  ctx.beginPath()
  ctx.moveTo(centerX, centerY)
  ctx.lineTo(centerX, canvasHeight - 20)
  ctx.stroke()
  
  // Y轴箭头
  ctx.setLineDash([]) // 实线
  ctx.fillStyle = '#888'
  ctx.beginPath()
  ctx.moveTo(centerX, 20)
  ctx.lineTo(centerX - 5, 30)
  ctx.lineTo(centerX + 5, 30)
  ctx.closePath()
  ctx.fill()
  
  // Y轴标签
  ctx.fillStyle = '#000'
  ctx.font = '12px Arial'
  ctx.fillText('Y', centerX + 8, 15)
  
  // 绘制坐标原点
  ctx.fillStyle = '#000'
  ctx.beginPath()
  ctx.arc(centerX, centerY, 3, 0, 2 * Math.PI)
  ctx.fill()
  
  // 绘制原点O
  ctx.fillStyle = '#000'
  ctx.font = '12px Arial'
  ctx.fillText('O', centerX + 5, centerY + 15)
}

// 绘制机械臂
const draw = () => {
  if (!ctx || !canvasRef.value) return
  
  ctx.clearRect(0, 0, canvasRef.value.width, canvasRef.value.height)
  
  const { L1, L2, L3, L4, distance, servoA, servoB } = armConfig.value
  
  // 舵机A（左）的固定点
  const servoA_X = -distance / 2
  const servoA_Y = 0
  
  // 舵机B（右）的固定点
  const servoB_X = distance / 2
  const servoB_Y = 0
  
  // 绘制坐标系
  drawCoordinateSystem()
  
  // 绘制可达区域
  if (showReachableArea.value) {
    drawReachableArea()
  }
  
  // 绘制运动轨迹
  if (props.trajectoryPoints && props.trajectoryPoints.length > 1) {
    ctx.strokeStyle = '#ff6b6b'
    ctx.lineWidth = 2
    ctx.setLineDash([5, 5]) // 虚线
    ctx.beginPath()
    
    // 绘制第一个点
    const firstPoint = props.trajectoryPoints[0]
    ctx.moveTo(centerX + firstPoint.x * scale.value, centerY - firstPoint.y * scale.value)
    
    // 连接所有点
    for (let i = 1; i < props.trajectoryPoints.length; i++) {
      const point = props.trajectoryPoints[i]
      ctx.lineTo(centerX + point.x * scale.value, centerY - point.y * scale.value)
    }
    
    ctx.stroke()
    ctx.setLineDash([]) // 恢复实线
    
    // 绘制轨迹点
    ctx.fillStyle = '#ff6b6b'
    props.trajectoryPoints.forEach((point, index) => {
      ctx.beginPath()
      ctx.arc(centerX + point.x * scale.value, centerY - point.y * scale.value, 4, 0, 2 * Math.PI)
      ctx.fill()
      
      // 在第一个和最后一个点上标注索引
      if (index === 0 || index === props.trajectoryPoints.length - 1) {
        ctx.fillStyle = '#000'
        ctx.font = '10px Arial'
        ctx.fillText(
          `${index}`,
          centerX + point.x * scale.value + 8,
          centerY - point.y * scale.value - 8
        )
        ctx.fillStyle = '#ff6b6b'
      }
    })
  }
  
  // 绘制基准线
  ctx.strokeStyle = '#000'
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(centerX + servoA_X * scale.value, centerY - servoA_Y * scale.value)
  ctx.lineTo(centerX + servoB_X * scale.value, centerY - servoB_Y * scale.value)
  ctx.stroke()
  
  // 将角度转换为弧度
  const angleA = servoA * Math.PI / 180
  const angleB = servoB * Math.PI / 180
  
  // 计算关节位置
  const jointA_X = servoA_X + L1 * Math.cos(angleA)
  const jointA_Y = servoA_Y + L1 * Math.sin(angleA)
  
  const jointB_X = servoB_X + L3 * Math.cos(angleB)
  const jointB_Y = servoB_Y + L3 * Math.sin(angleB)
  
  // 计算末端执行器位置
  const dx = jointB_X - jointA_X
  const dy = jointB_Y - jointA_Y
  const dist = Math.sqrt(dx ** 2 + dy ** 2)
  
  const a = (L2 ** 2 - L4 ** 2 + dist ** 2) / (2 * dist)
  const h = Math.sqrt(L2 ** 2 - a ** 2)
  
  const px = jointA_X + a * dx / dist
  const py = jointA_Y + a * dy / dist
  
  const ex = px - h * dy / dist
  const ey = py + h * dx / dist
  
  // 绘制舵机A的手臂
  ctx.strokeStyle = '#666'
  ctx.lineWidth = 3
  ctx.beginPath()
  ctx.moveTo(centerX + servoA_X * scale.value, centerY - servoA_Y * scale.value)
  ctx.lineTo(centerX + jointA_X * scale.value, centerY - jointA_Y * scale.value)
  ctx.stroke()
  
  // 绘制舵机B的手臂
  ctx.beginPath()
  ctx.moveTo(centerX + servoB_X * scale.value, centerY - servoB_Y * scale.value)
  ctx.lineTo(centerX + jointB_X * scale.value, centerY - jointB_Y * scale.value)
  ctx.stroke()
  
  // 绘制连杆
  ctx.beginPath()
  ctx.moveTo(centerX + jointA_X * scale.value, centerY - jointA_Y * scale.value)
  ctx.lineTo(centerX + ex * scale.value, centerY - ey * scale.value)
  ctx.stroke()
  
  ctx.beginPath()
  ctx.moveTo(centerX + jointB_X * scale.value, centerY - jointB_Y * scale.value)
  ctx.lineTo(centerX + ex * scale.value, centerY - ey * scale.value)
  ctx.stroke()
  
  // 绘制固定点（红色圆）
  ctx.fillStyle = '#f00'
  ctx.beginPath()
  ctx.arc(centerX + servoA_X * scale.value, centerY - servoA_Y * scale.value, 5, 0, 2 * Math.PI)
  ctx.fill()
  
  ctx.beginPath()
  ctx.arc(centerX + servoB_X * scale.value, centerY - servoB_Y * scale.value, 5, 0, 2 * Math.PI)
  ctx.fill()
  
  // 绘制中间关节（蓝色圆）
  ctx.fillStyle = '#00f'
  ctx.beginPath()
  ctx.arc(centerX + jointA_X * scale.value, centerY - jointA_Y * scale.value, 4, 0, 2 * Math.PI)
  ctx.fill()
  
  ctx.beginPath()
  ctx.arc(centerX + jointB_X * scale.value, centerY - jointB_Y * scale.value, 4, 0, 2 * Math.PI)
  ctx.fill()
  
  // 绘制末端执行器（绿色圆）
  ctx.fillStyle = '#0f0'
  ctx.beginPath()
  ctx.arc(centerX + ex * scale.value, centerY - ey * scale.value, 6, 0, 2 * Math.PI)
  ctx.fill()
  
  // 更新末端位置
  endEffectorPosition.value = { x: ex, y: ey }
}

// 绘制可达区域
const drawReachableArea = () => {
  if (!ctx || !canvasRef.value) return
  
  const { L1, L2, L3, L4, distance } = armConfig.value
  
  // 绘制两个臂杆可达区域的交集
  ctx.fillStyle = 'rgba(173, 216, 230, 0.3)'
  ctx.strokeStyle = 'rgba(100, 150, 200, 0.5)'
  ctx.lineWidth = 1
  
  // 舵机A（左）的固定点
  const servoA_X = -distance / 2
  const servoA_Y = 0
  
  // 舵机B（右）的固定点
  const servoB_X = distance / 2
  const servoB_Y = 0
  
  // 绘制两个工作区域的交集（使用路径裁剪）
  ctx.beginPath()
  
  // 舵机A的工作空间
  const radiusA_min = Math.abs(L1 - L2)
  const radiusA_max = L1 + L2
  ctx.arc(centerX + servoA_X * scale.value, centerY - servoA_Y * scale.value, radiusA_max * scale.value, 0, 2 * Math.PI)
  
  // 舵机B的工作空间
  const radiusB_min = Math.abs(L3 - L4)
  const radiusB_max = L3 + L4
  ctx.arc(centerX + servoB_X * scale.value, centerY - servoB_Y * scale.value, radiusB_max * scale.value, 0, 2 * Math.PI)
  
  ctx.fill()
  ctx.stroke()
  
  // 绘制最小可达区域
  ctx.fillStyle = 'rgba(200, 200, 200, 0.2)'
  ctx.beginPath()
  ctx.arc(centerX + servoA_X * scale.value, centerY - servoA_Y * scale.value, radiusA_min * scale.value, 0, 2 * Math.PI)
  ctx.arc(centerX + servoB_X * scale.value, centerY - servoB_Y * scale.value, radiusB_min * scale.value, 0, 2 * Math.PI)
  ctx.fill()
}

// 鼠标事件处理
const handleMouseDown = (e) => {
  if (!canvasRef.value) return
  const rect = canvasRef.value.getBoundingClientRect()
  const x = (e.clientX - rect.left - centerX) / scale.value
  const y = -(e.clientY - rect.top - centerY) / scale.value
  
  // 检查是否点击在末端执行器附近
  const dx = x - endEffectorPosition.value.x
  const dy = y - endEffectorPosition.value.y
  const dist = Math.sqrt(dx ** 2 + dy ** 2)
  
  if (dist < 1) {
    isDragging.value = true
  }
}

const handleMouseMove = (e) => {
  if (!isDragging.value || !canvasRef.value) return
  
  const rect = canvasRef.value.getBoundingClientRect()
  const x = (e.clientX - rect.left - centerX) / scale.value
  const y = -(e.clientY - rect.top - centerY) / scale.value
  
  // 计算逆运动学
  const angles = inverseKinematics(x, y)
  
  if (angles) {
    armConfig.value.servoA = angles.servoA
    armConfig.value.servoB = angles.servoB
    
    // 不再自动更新servos的结束角度，只能通过"设置当前角度"按钮来改变
    
    // 更新末端位置
    endEffectorPosition.value.x = x
    endEffectorPosition.value.y = y
    
    draw()
  }
}

const handleMouseUp = () => {
  isDragging.value = false
  
  // 拖动结束时，立即发送最后一次位置更新
  if (throttleTimer) {
    clearTimeout(throttleTimer)
    throttleTimer = null
  }
  
  if (!isUpdatingFromProps.value) {
    emit('end-position-changed', {
      x: Number(endEffectorPosition.value.x.toFixed(1)),
      y: Number(endEffectorPosition.value.y.toFixed(1))
    })
  }
}

const handleWheel = (e) => {
  e.preventDefault()
  const delta = e.deltaY
  if (delta > 0) {
    zoomOut()
  } else {
    zoomIn()
  }
}

const zoomIn = () => {
  if (zoom.value < 3) {
    zoom.value += 0.1
    draw()
  }
}

const zoomOut = () => {
  if (zoom.value > 0.3) {
    zoom.value -= 0.1
    draw()
  }
}

const resetZoom = () => {
  zoom.value = 1
  draw()
}

const toggleReachableArea = () => {
  showReachableArea.value = !showReachableArea.value
  draw()
}

// 保存轨迹
const saveTrajectory = () => {
  if (!canSaveTrajectory.value) {
    alert('请先选择设备和舵机版本')
    return
  }
  
  // 获取当前舵机列表
  const servoConfig = servoList[props.servoVersion]
  if (!servoConfig) {
    alert('找不到对应的舵机配置')
    return
  }
  
  // 提示用户输入轨迹名称
  const trajectoryName = prompt('请输入轨迹名称:', `轨迹_${new Date().getTime()}`)
  if (!trajectoryName) return
  
  // 获取当前选中的舵机ID（使用前两个舵机）
  const servoIds = [1, 2]
  
  // 准备轨迹数据
  const trajectoryData = {
    servoVersions: [props.servoVersion],
    ids: servoIds,
    deviceId: props.deviceId,
    position: {
      x: endEffectorPosition.value.x,
      y: endEffectorPosition.value.y
    },
    sailServoAngleA: armConfig.value.servoA,
    sailServoAngleB: armConfig.value.servoB,
    servoAid: servoIds[0],
    servoBid: servoIds[1],
    armLengths: {
      L1: armConfig.value.L1,
      L2: armConfig.value.L2,
      L3: armConfig.value.L3,
      L4: armConfig.value.L4
    },
    servoDistance: armConfig.value.distance,
    timestamp: new Date().toISOString()
  }
  
  // 打印数据以供调试
  console.log('保存轨迹:', trajectoryName, trajectoryData)
  
  // 触发事件通知父组件
  emit('trajectory-saved', {
    name: trajectoryName,
    data: trajectoryData
  })
  
  alert('轨迹已保存')
}

// 监听配置变化
watch(() => armConfig.value.L1, draw)
watch(() => armConfig.value.L2, draw)
watch(() => armConfig.value.L3, draw)
watch(() => armConfig.value.L4, draw)
watch(() => armConfig.value.distance, draw)
watch(() => armConfig.value.servoA, draw)
watch(() => armConfig.value.servoB, draw)
watch(() => zoom.value, draw)
watch(() => props.trajectoryPoints, draw, { deep: true })
watch(() => showTrajectoryChart.value, (val) => {
  if (val) {
    nextTick(() => renderChart())
  }
})

watch(servos, (newServos) => {
  if (showTrajectoryChart.value) {
    renderChart()
  }
  // 将servos变化传递给父组件
  emit('servos-changed', newServos)
}, { deep: true })

// 监听allServosData变化，重新渲染图表
watch(() => props.allServosData, () => {
  if (showTrajectoryChart.value) {
    renderChart()
  }
}, { deep: true })

// 监听armConfig角度变化，同步到servos
// 不再自动更新servos的角度，只能通过"设置当前角度"按钮来改变

// 监听armConfig的变化，emit给父组件
watch(() => armConfig.value, (newConfig) => {
  emit('arm-config-changed', newConfig)
}, { deep: true })

// 监听showArmVisualization变化，重新初始化canvas
watch(() => props.showArmVisualization, (newVal) => {
  if (newVal) {
    // 当显示机械臂可视化时，等待DOM更新后重新初始化canvas
    nextTick(() => {
      if (canvasRef.value) {
        initCanvas()
        draw()
      }
    })
  }
})

// 监听轨迹参数变化，更新机械臂位置
watch(() => props.trajectoryX, (newX) => {
  if (newX !== undefined && props.trajectoryY !== undefined && !isUpdatingFromProps.value) {
    isUpdatingFromProps.value = true
    const angles = inverseKinematics(newX, props.trajectoryY)
    if (angles) {
      armConfig.value.servoA = angles.servoA
      armConfig.value.servoB = angles.servoB
      endEffectorPosition.value.x = newX
      endEffectorPosition.value.y = props.trajectoryY
      draw()
    }
    nextTick(() => {
      isUpdatingFromProps.value = false
    })
  }
})

watch(() => props.trajectoryY, (newY) => {
  if (props.trajectoryX !== undefined && newY !== undefined && !isUpdatingFromProps.value) {
    isUpdatingFromProps.value = true
    const angles = inverseKinematics(props.trajectoryX, newY)
    if (angles) {
      armConfig.value.servoA = angles.servoA
      armConfig.value.servoB = angles.servoB
      endEffectorPosition.value.x = props.trajectoryX
      endEffectorPosition.value.y = newY
      draw()
    }
    nextTick(() => {
      isUpdatingFromProps.value = false
    })
  }
})

// 节流器，限制emit频率
let throttleTimer = null
function throttledEmitPosition(newPos) {
  if (throttleTimer) return
  
  throttleTimer = setTimeout(() => {
    emit('end-position-changed', {
      x: Number(newPos.x.toFixed(1)),
      y: Number(newPos.y.toFixed(1))
    })
    throttleTimer = null
  }, 100) // 每100ms最多emit一次
}

// 监听末端位置变化，emit给父组件（用于更新轨迹参数显示）
watch(() => endEffectorPosition.value, (newPos) => {
  if (!isUpdatingFromProps.value) {
    throttledEmitPosition(newPos)
  }
}, { deep: true })

// 初始化舵机列表
const initServos = () => {
  if (props.trajectoryConfig && props.trajectoryConfig.ids) {
    const servoIds = props.trajectoryConfig.ids
    const servoVersions = props.trajectoryConfig.servoVersions || []
    
    // 优先从 origin 读取坐标，如果没有则从 x, y 读取（向后兼容）
    const initialPosition = getOriginPosition(props.trajectoryConfig, 'origin')
    if (initialPosition.x !== 0 || initialPosition.y !== 0 || props.trajectoryConfig.x !== undefined || props.trajectoryConfig.y !== undefined) {
      endEffectorPosition.value.x = initialPosition.x
      endEffectorPosition.value.y = initialPosition.y
      
      // 通过逆运动学计算舵机角度
      const angles = inverseKinematics(initialPosition.x, initialPosition.y)
      if (angles) {
        armConfig.value.servoA = angles.servoA
        armConfig.value.servoB = angles.servoB
      }
    }
    
    servos.value = servoIds.map((id, idx) => {
      // 从servoList中查找舵机信息
      let servoInfo = null
      for (const version of servoVersions) {
        if (servoList[version]) {
          servoInfo = servoList[version].find(s => s.id === id)
          if (servoInfo) break
        }
      }
      
      const initialAngle = getKinematicAngleForPhysicalServoIndex(
        props.trajectoryConfig,
        idx,
        armConfig.value
      )

      return {
        s: id,
        name: servoInfo ? servoInfo.name : `舵机${id}`,
        range: servoInfo ? servoInfo.range : [-200, 200],
        l: 1,
        m: [{steps: 10, param: [initialAngle, initialAngle, initialAngle, initialAngle]}]
      }
    })
  } else if (servos.value.length === 0) {
    // 如果没有轨迹配置，创建默认的双舵机配置
    const defaultInitialAngle = 0
    servos.value = [
      {
        s: 1,
        name: '舵机A(左)',
        range: [-200, 200],
        l: 1,
        m: [{steps: 10, param: [defaultInitialAngle, defaultInitialAngle, defaultInitialAngle, defaultInitialAngle]}]
      },
      {
        s: 2,
        name: '舵机B(右)',
        range: [-200, 200],
        l: 1,
        m: [{steps: 10, param: [defaultInitialAngle, defaultInitialAngle, defaultInitialAngle, defaultInitialAngle]}]
      }
    ]
  }
}

// 添加运动片段（支持成对添加）
const addMotion = (idx) => {
  const mArr = servos.value[idx].m
  const lastEnd = mArr.length > 0 ? mArr[mArr.length - 1].param[1] : 0
  mArr.push({steps: 10, param: [lastEnd, lastEnd, lastEnd, lastEnd]})
  
  // 成对添加：如果servos长度为2，则在另一个舵机上也添加片段
  if (servos.value.length === 2) {
    const otherIdx = idx === 0 ? 1 : 0
    const otherMArr = servos.value[otherIdx].m
    const otherLastEnd = otherMArr.length > 0 ? otherMArr[otherMArr.length - 1].param[1] : 0
    otherMArr.push({steps: 10, param: [otherLastEnd, otherLastEnd, otherLastEnd, otherLastEnd]})
  }
  
  renderChart()
}

// 删除运动片段（支持成对删除）
const removeMotion = (idx, mIdx) => {
  servos.value[idx].m.splice(mIdx, 1)
  
  // 成对删除：如果servos长度为2，则在另一个舵机上也删除对应索引的片段
  if (servos.value.length === 2 && servos.value[idx].m.length > mIdx) {
    const otherIdx = idx === 0 ? 1 : 0
    const otherMArr = servos.value[otherIdx].m
    if (otherMArr.length > mIdx) {
      otherMArr.splice(mIdx, 1)
    }
  }
  
  renderChart()
}

// 删除舵机
const removeServo = (idx) => {
  servos.value.splice(idx, 1)
  renderChart()
}

// 更新位置（从输入框）
const updatePositionFromInput = () => {
  if (editableX.value !== undefined && editableY.value !== undefined) {
    // 通过逆运动学计算舵机角度
    const angles = inverseKinematics(editableX.value, editableY.value)
    
    if (angles) {
      armConfig.value.servoA = angles.servoA
      armConfig.value.servoB = angles.servoB
      
      // 更新末端位置
      endEffectorPosition.value.x = editableX.value
      endEffectorPosition.value.y = editableY.value
      
      // 不再自动更新servos的结束角度，只能通过"设置当前角度"按钮来改变
      
      draw()
    }
  }
}

// 设置当前角度
const setCurrentAngle = (idx, mIdx) => {
  const cfg = props.trajectoryConfig
  const angle = cfg
    ? getKinematicAngleForPhysicalServoIndex(cfg, idx, armConfig.value)
    : (idx === 0 ? armConfig.value.servoA : armConfig.value.servoB)
  if (servos.value[idx]?.m?.length > mIdx) {
    servos.value[idx].m[mIdx].param[1] = angle
  }
  renderChart()
}

// 获取舵机结束角度（绑定到armConfig）
const getServoEndAngle = (idx) => {
  const cfg = props.trajectoryConfig
  if (cfg && servos.value.length >= 2) {
    return getKinematicAngleForPhysicalServoIndex(cfg, idx, armConfig.value)
  }
  if (idx === 0) return armConfig.value.servoA
  if (idx === 1) return armConfig.value.servoB
  return servos.value[idx]?.m[0]?.param[1] || 0
}

// 更新舵机结束角度（同步到armConfig）
const updateServoEndAngle = (idx, value) => {
  const angle = parseFloat(value)
  if (isNaN(angle)) return

  const cfg = props.trajectoryConfig
  if (cfg && servos.value.length >= 2) {
    setKinematicArmFromPhysicalServoIndex(cfg, idx, angle, armConfig.value)
  } else if (idx === 0) {
    armConfig.value.servoA = angle
  } else if (idx === 1) {
    armConfig.value.servoB = angle
  }

  if (servos.value[idx]?.m?.length > 0) {
    servos.value[idx].m[0].param[1] = angle
  }

  draw()
  renderChart()
}

// 曲线公式：通过四点反推三次多项式系数
function solveCubicCoefficients(x0, y0, x1, y1, x2, y2, x3, y3) {
  const A = [
    [Math.pow(x0, 3), Math.pow(x0, 2), x0, 1],
    [Math.pow(x1, 3), Math.pow(x1, 2), x1, 1],
    [Math.pow(x2, 3), Math.pow(x2, 2), x2, 1],
    [Math.pow(x3, 3), Math.pow(x3, 2), x3, 1]
  ];
  const Y = [y0, y1, y2, y3];

  function det4(m) {
    return (
        m[0][3] * m[1][2] * m[2][1] * m[3][0] - m[0][2] * m[1][3] * m[2][1] * m[3][0] -
        m[0][3] * m[1][1] * m[2][2] * m[3][0] + m[0][1] * m[1][3] * m[2][2] * m[3][0] +
        m[0][2] * m[1][1] * m[2][3] * m[3][0] - m[0][1] * m[1][2] * m[2][3] * m[3][0] -
        m[0][3] * m[1][2] * m[2][0] * m[3][1] + m[0][2] * m[1][3] * m[2][0] * m[3][1] +
        m[0][3] * m[1][0] * m[2][2] * m[3][1] - m[0][0] * m[1][3] * m[2][2] * m[3][1] -
        m[0][2] * m[1][0] * m[2][3] * m[3][1] + m[0][0] * m[1][2] * m[2][3] * m[3][1] +
        m[0][3] * m[1][1] * m[2][0] * m[3][2] - m[0][1] * m[1][3] * m[2][0] * m[3][2] -
        m[0][3] * m[1][0] * m[2][1] * m[3][2] + m[0][0] * m[1][3] * m[2][1] * m[3][2] +
        m[0][1] * m[1][0] * m[2][3] * m[3][2] - m[0][0] * m[1][1] * m[2][3] * m[3][2] -
        m[0][2] * m[1][1] * m[2][0] * m[3][3] + m[0][1] * m[1][2] * m[2][0] * m[3][3] +
        m[0][2] * m[1][0] * m[2][1] * m[3][3] - m[0][0] * m[1][2] * m[2][1] * m[3][3] -
        m[0][1] * m[1][0] * m[2][2] * m[3][3] + m[0][0] * m[1][1] * m[2][2] * m[3][3]
    );
  }

  function replaceCol(m, colIdx, newCol) {
    return m.map((row, i) => row.map((v, j) => (j === colIdx ? newCol[i] : v)));
  }

  const D = det4(A);
  if (Math.abs(D) < 1e-8) return [0, 0, 0, 0];
  const D0 = det4(replaceCol(A, 0, Y));
  const D1 = det4(replaceCol(A, 1, Y));
  const D2 = det4(replaceCol(A, 2, Y));
  const D3 = det4(replaceCol(A, 3, Y));
  return [D0 / D, D1 / D, D2 / D, D3 / D];
}

function getCurveValue(coeffs, x) {
  const [k3, k2, k1, k0] = coeffs;
  return k3 * Math.pow(x, 3) + k2 * Math.pow(x, 2) + k1 * x + k0;
}

// 渲染ECharts图表
const renderChart = () => {
  if (!chartRef.value || !showTrajectoryChart.value) return
  
  if (!chartInstance) {
    chartInstance = echarts.init(chartRef.value)
  }
  
  // 使用allServosData渲染图表
  const series = props.allServosData.map((servo, sIdx) => {
    let points = []
    let step = 0
    
    servo.m.forEach((motion) => {
      const steps = motion.steps
      const start = motion.param[0]
      const end = motion.param[1]
      const y1 = motion.param[2] !== undefined ? motion.param[2] : start + (end - start) / 3
      const y2 = motion.param[3] !== undefined ? motion.param[3] : start + 2 * (end - start) / 3
      
      // 四点坐标
      const x0 = 0, y0 = start
      const x1 = steps / 3, x2 = 2 * steps / 3
      const x3 = steps, y3 = end
      
      // 使用与App.vue相同的曲线计算
      const coeffs = solveCubicCoefficients(x0, y0, x1, y1, x2, y2, x3, y3)
      const isLastMotion = false // 简化处理
      const loopEnd = isLastMotion ? steps : steps - 1
      
      for (let i = 0; i <= loopEnd; i++) {
        const val = getCurveValue(coeffs, i)
        points.push([step, val])
        step++
      }
    })
    
    return {
      name: servo.name,
      type: 'line',
      data: points,
      showSymbol: true,
      symbolSize: 1,
      emphasis: {focus: 'series'},
      universalTransition: true
    }
  })
  
  const option = {
    tooltip: {trigger: 'axis'},
    legend: {},
    xAxis: {
      type: 'value',
      name: '步数(steps)',
      splitLine: {show: false}
    },
    yAxis: {
      type: 'value',
      name: '位置',
      splitLine: {show: false}
    },
    series
  }
  
  chartInstance.setOption(option, true)
}

onMounted(() => {
  initServos()
  initCanvas()
  nextTick(() => {
    if (showTrajectoryChart.value) {
      renderChart()
    }
  })
})

onBeforeUnmount(() => {
  // 清理定时器
  if (throttleTimer) {
    clearTimeout(throttleTimer)
    throttleTimer = null
  }
})
</script>

<style scoped>
</style>

